import {Component, Input, OnChanges} from '@angular/core';
import {formatCurrency} from '@angular/common';
import {TranslateService} from '@ngx-translate/core';
import {decimalInput, fieldError, fieldErrors, InvoicingService} from '../../shared/invoicing.service';
import {ProjectInvoicing, VatOption} from '../../interfaces/invoicing';

/** A project's VAT option, client reference (#1516) and km sales price (#1517), with what still blocks invoicing it. Admin only. */
@Component({
  selector: 'bst-invoicing-project',
  templateUrl: './invoicing-project.component.html',
  styles: [`
    .required { margin-left: 2px; color: var(--color-danger-default); }
    ul.field-list > li { flex-wrap: wrap; }
    ul.field-list > li > input, ul.field-list > li > nb-select { flex: 1; min-width: 0; max-width: 24rem; }
    .with-unit { display: flex; align-items: center; gap: 8px; }
    .with-unit input { width: 6rem; }
    .unit { font-weight: 400; color: var(--text-hint-color); }
    ul.field-list > li > .hint, ul.field-list > li > .error { flex-basis: 100%; padding: 4px 0 0 189px; font-weight: 400; }
    @media (max-width: 767px) { ul.field-list > li > .hint, ul.field-list > li > .error { padding-left: 0; } }
    .error { flex-basis: 100%; padding-top: 4px; font-size: 0.8rem; font-weight: 400; color: var(--color-danger-default); }
    .readiness { display: flex; align-items: flex-start; gap: 8px; margin-top: 12px; font-size: 0.9rem; }
    .readiness nb-icon { flex-shrink: 0; font-size: 1.1rem; }
    .readiness ul { margin: 4px 0 0; padding-left: 1.1rem; }
    .actions { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-top: 16px; }
    .hint { color: var(--text-hint-color); font-size: 0.85rem; }
  `],
  standalone: false
})
export class InvoicingProjectComponent implements OnChanges {
  @Input() projectId: number;
  // A deep link from an invoice issue (?field=vat_option); focused once the project loads.
  @Input() focusField: string | null = null;
  project: ProjectInvoicing = null;
  options: VatOption[] = [];
  errors: Record<string, string[]> = {};
  busy = false;

  constructor(private invoicing: InvoicingService, private translate: TranslateService) {}

  // The UI language, passed to formatCurrency: LOCALE_ID is fixed at bootstrap.
  get lang(): string {
    return this.translate.currentLang ?? this.translate.defaultLang;
  }

  // Members assigned at the rate a new user gets, which may be a placeholder; invoicing still goes, as on the draft.
  get defaultRate(): {rate: string, people: string} | null {
    const people = this.project?.default_rate ?? [];
    return people.length ? {rate: formatCurrency(+this.project.default_member_rate, this.lang, '€', 'EUR'), people: people.join(', ')} : null;
  }

  // The project page is reused from one project to the next, so load on every new id.
  async ngOnChanges() {
    const id = this.projectId;
    this.project = null;
    this.errors = {};
    const [project, options] = await Promise.all([this.invoicing.getProject(id), this.invoicing.getVatOptions()])
      .catch((): [ProjectInvoicing, VatOption[]] => [null, []]);
    if (id !== this.projectId) { return; }
    [this.project, this.options] = [project, options];
    this.focus(this.focusField);
  }

  focus(field: string | null) {
    if (!field || !this.project) { return; }
    // After this render: the field may have only just appeared.
    setTimeout(() => {
      const el = document.getElementById('project-' + field);
      (el?.querySelector('button') ?? el)?.focus();
    });
  }

  decimal(value: string | null | undefined): string {
    return decimalInput(this.translate, value);
  }

  error(field: string): string | null {
    return fieldError(this.translate, this.errors, field);
  }

  async save() {
    if (this.busy) { return; }
    this.busy = true;
    this.errors = {};
    const saved = await this.invoicing.saveProject(this.projectId, this.project).catch(e => { this.errors = fieldErrors(e); return null; });
    this.busy = false;
    if (saved) { this.project = saved; }
  }
}
